import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ExternalLink } from "lucide-react";
import Seo from "@/components/Seo";
import PageIntro from "@/components/PageIntro";
import { Reveal, EASE } from "@/components/landing/Reveal";
import { ABOUT_MISSION, MISSION_REFERENCES } from "@/data/aboutMission";
import { AboutAuthor } from "@/components/about/AboutAuthor";
import { scriptureText, scriptureLink } from "@/data/scripture";

const SYMBOLS = [
  {
    n: "01",
    title: "The mandorla",
    text: "The deep-blue almond-shaped field is the mandorla — an ancient sign of heavenly glory in Eastern Christian art. It surrounds Christ in the Transfiguration and Mary in the Dormition. In this emblem, blue represents her humanity clothed in divine grace.",
  },
  {
    n: "02",
    title: "The Marian M",
    text: "The gold monogram M is the Latin Marian cypher — the letter of the Miraculous Medal of 1830, the seal of countless Catholic churches. It anchors the emblem in the West and expresses this treasury’s purpose: through Mary to Jesus.",
  },
  {
    n: "03",
    title: "The three-bar cross",
    text: "The cross crowning the mandorla is the Suppedaneum Cross of the Eastern Churches — Byzantine, Slavic, and Melkite. Its upper bar is the titulus ('INRI'), its lower slanted bar the footrest that points to the two thieves: one up, one down. It marks the emblem as belonging to both lungs of the Church.",
  },
  {
    n: "04",
    title: "Twelve stars",
    text: "Twelve stars recall the tribes of Israel and the apostles. In this emblem, they remind us that Mary is the Mother of the whole Church. The biblical image is found in Revelation 12:1.",
    scripture: "Revelation 12:1",
  },
  {
    n: "05",
    title: "The lily",
    text: "On the left, a Madonna lily — the flower of the Annunciation, symbol of Mary's virginity, painted at the Angel's feet in a thousand Western altarpieces from Fra Angelico onwards.",
  },
  {
    n: "06",
    title: "The rose",
    text: "On the right, a golden rose — the flower given by the Pope every fourth Sunday of Lent, and by the Mother of God herself to Juan Diego in December 1531. 'Rosa Mystica' is one of the titles of the Litany of Loreto.",
  },
  {
    n: "07",
    title: "Blue, gold, and ivory",
    text: "The palette is the Church's own: Marian blue for the mantle of the Theotokos; gold for the Light of God; ivory for the parchment of Scripture on which every doctrine of Mary is written. No modern colour, nothing decorative for its own sake.",
  },
];

const ROADMAP = [
  ["Now — Foundations", "Seventeen apparition and devotional accounts, four sourced dogma guides, the illustrated Rosary in four languages, the shared prayer treasury, Roman calendars and selected Eastern feasts, editorial standards, and the East & West chapter."],
  ["Soon — Depth", "A growing library of Church documents; complete local Eastern ordines; further prayer editions and carefully reviewed translations; spoken Rosary and Paraklesis audio; more shrines from Asia and Africa."],
  ["Later — Community", "Verified contributors, editorial roles for scholars and pastors, translated pages reviewed by native speakers, an atlas of every recognised Marian shrine in the world."],
];

export default function AboutPage() {
  return (
    <main data-testid="about-page">
      <Seo
        title="About Fred Bachour & Theotokos Mater Dei"
        siteName="Theotokos · Mater Dei"
        description="Meet Fred Bachour and discover Theotokos Mater Dei, a work of The Gate to Heaven Ministry dedicated to Marian prayer, learning and faith in Jesus Christ."
      />
      <PageIntro
        crumbs={[{ label: "Home", to: "/" }, { label: "About" }]}
        eyebrow="About the treasury"
        lines={["A Catholic treasury,", "a shared mission"]}
        aside={
          <div className="rounded-2xl border border-marian/15 bg-white/70 p-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">Our promise</p>
            <p className="mt-2 font-serif text-lg leading-snug text-midnight">
              Non-commercial. No intrusive advertising. If donations are ever needed, finances will be transparent — and we will never sell promises, blessings, or spiritual favours.
            </p>
          </div>
        }
      >
        A place of study, prayer, and beauty about the Blessed Virgin Mary — Latin and Eastern
        Catholic, one Mother, one Church. Inspired by the example of St. Carlo Acutis: to
        gather what the Church herself has said, sourced with care, and to offer it freely.
      </PageIntro>

      <AboutAuthor />

      {/* Mission */}
      <section className="mx-auto max-w-7xl scroll-mt-28 px-4 py-14 sm:px-8 sm:py-20 lg:px-16" id="mission" data-testid="about-mission">
        <div className="grid gap-12 lg:grid-cols-[1.1fr_0.9fr]">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-gold">Chapter 01 · A lay Catholic’s mission</p>
            <h2 className="mt-3 font-serif text-3xl font-semibold text-midnight sm:text-4xl" data-testid="about-mission-heading">Why we exist</h2>
            <div className="mt-6 space-y-5 text-base leading-relaxed text-charcoal/90 sm:text-lg" data-testid="about-mission-story">
              {ABOUT_MISSION.map((paragraph, i) => <p key={i} data-testid={`about-mission-paragraph-${i + 1}`}>{paragraph}</p>)}
            </div>
            <div className="mt-7 space-y-3 border-t border-marian/15 pt-5" data-testid="about-mission-references">
              {MISSION_REFERENCES.map((reference, i) => <a key={reference.url} href={reference.url} target="_blank" rel="noopener noreferrer" data-testid={`about-mission-reference-${i + 1}`} className="flex items-start gap-2 text-xs leading-relaxed text-marian underline decoration-marian/30 underline-offset-4 transition-colors hover:text-gold"><span>{reference.title}</span><ExternalLink size={12} className="mt-0.5 shrink-0" aria-hidden="true" /></a>)}
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <ol className="not-prose space-y-4" data-testid="roadmap-list">
              {ROADMAP.map(([label, text], i) => (
                <li key={label} className="rounded-2xl border border-marian/15 bg-white/85 p-5">
                  <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold">Stage {i + 1}</p>
                  <p className="mt-1 font-serif text-2xl font-semibold text-midnight">{label}</p>
                  <p className="mt-2 text-sm leading-relaxed text-charcoal/85">{text}</p>
                </li>
              ))}
            </ol>
          </Reveal>
        </div>
      </section>

      {/* Emblem */}
      <section className="border-t border-marian/15 bg-paleblue/40 py-14 sm:py-20" id="emblem">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold">Chapter 02 · The emblem</p>
            <h2 className="mt-3 font-serif text-3xl font-semibold tracking-tight text-midnight sm:text-4xl">A coat of arms for the Mother of God</h2>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-charcoal/85">
              Every symbol in the emblem of Theotokos · Mater Dei is drawn from the Church's own
              vocabulary. It unites East and West in a single image; nothing is decorative alone.
            </p>
          </Reveal>

          <motion.figure
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-70px" }}
            transition={{ duration: 1.0, ease: EASE }}
            className="mt-10 overflow-hidden rounded-3xl border border-marian/15 bg-white shadow-[0_40px_90px_-40px_rgba(16,42,67,0.5)]"
            data-testid="about-emblem-figure"
          >
            <div className="gold-rule" />
            <div className="flex items-center justify-center bg-ivory p-6 sm:p-10">
              <img
                src="/emblem-full.png"
                alt="The full emblem of Theotokos · Mater Dei — a blue mandorla with the Marian M, crowned by a three-bar cross, framed by twelve stars, a lily and a rose"
                className="mx-auto w-full max-w-2xl"
                loading="lazy"
              />
            </div>
            <figcaption className="border-t border-marian/10 px-6 py-4 text-center font-mono text-[10px] uppercase tracking-[0.28em] text-slateblue">
              Theotokos · Mater Dei — Mother of God · One Church
            </figcaption>
          </motion.figure>

          <ol className="mt-12 grid gap-4 md:grid-cols-2" data-testid="emblem-symbols">
            {SYMBOLS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.05} className="h-full">
                <li className="card-lift h-full rounded-2xl border border-marian/15 bg-white p-6" data-testid={`emblem-symbol-${s.n}`}>
                  <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold">Symbol {s.n}</p>
                  <p className="mt-2 font-serif text-xl font-semibold text-midnight">{s.title}</p>
                  <p data-testid={`emblem-symbol-text-${s.n}`} className="mt-3 text-sm leading-relaxed text-charcoal/85">{s.text}</p>
                  {s.scripture && <figure className="mt-4 border-l-2 border-gold/60 pl-4">
                    <blockquote data-testid={`emblem-scripture-${s.n}`} className="font-serif text-base leading-relaxed text-midnight">{scriptureText(s.scripture)}</blockquote>
                    <figcaption className="mt-2"><a href={scriptureLink(s.scripture)} target="_blank" rel="noopener noreferrer" data-testid={`emblem-scripture-source-${s.n}`} className="text-xs text-marian underline underline-offset-4 transition-colors hover:text-gold">{s.scripture} · RSV-CE</a></figcaption>
                  </figure>}
                </li>
              </Reveal>
            ))}
          </ol>

          <Reveal delay={0.1}>
            <p className="mt-10 max-w-2xl text-sm italic leading-relaxed text-slateblue" data-testid="emblem-note">
              The emblem was drawn especially for this treasury: nothing in it is stock, nothing is
              generic. If you would like to use it for a parish exhibition or for a Catholic
              publication, please write to us — we grant permission freely for reverent, non-commercial use.
            </p>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-8 lg:px-16">
        <Reveal>
          <div className="grid gap-6 rounded-3xl bg-midnight px-6 py-10 text-ivory sm:grid-cols-[1fr_auto] sm:items-center sm:px-10">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold">Ad Iesum per Mariam</p>
              <p className="mt-2 font-serif text-2xl leading-snug sm:text-3xl">A treasury is built by many hands. If you can help — a correction, a translation, a scholar's note — we would be honoured.</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to="/contact" data-testid="about-cta-contact" className="group inline-flex items-center gap-2 rounded-full bg-gold px-5 py-3 text-sm font-semibold text-midnight transition-colors hover:bg-ivory">
                Write to us
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link to="/editorial-standards" data-testid="about-cta-standards" className="inline-flex items-center gap-2 rounded-full border border-ivory/30 px-5 py-3 text-sm font-semibold text-ivory transition-colors hover:border-gold hover:text-gold">
                Editorial standards
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
